/**
 * Media paths. Content stores images either as public paths (`/uploads/x.webp`, served from `public/`) or relative to
 * the entry file (`./images/x.webp`, for Astro's `image()`). These helpers convert between the value in the content
 * and the file's path in the repo.
 */
import type { MediaConfig } from './config.js';

/** A video kept outside the repository (a gallery item uploaded to R2): its public https address. */
export const isVideoUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\/[^\s?#]+\.(mp4|mov|m4v|webm)([?#].*)?$/i.test(value);

export function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i < 0 ? '' : path.slice(0, i);
}

export function basename(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

export function joinPath(...parts: string[]): string {
  const out: string[] = [];
  for (const part of parts.join('/').split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') out.pop();
    else out.push(part);
  }
  return out.join('/');
}

/**
 * Repo path of an image value. `entryPath` is the content file that holds the value.
 * Returns undefined for external URLs.
 */
export function resolveMediaPath(value: string, entryPath: string, media?: MediaConfig): string | undefined {
  if (!value || /^[a-z]+:\/\//i.test(value) || value.startsWith('data:')) return undefined;
  if (value.startsWith('/')) {
    // Public path: `/uploads/x` → `public/uploads/x`, or a media folder whose public path matches.
    if (media && media.public.startsWith('/') && value.startsWith(media.public.replace(/\/$/, '') + '/')) {
      return joinPath(media.folder, value.slice(media.public.replace(/\/$/, '').length));
    }
    return joinPath('public', value);
  }
  return joinPath(dirname(entryPath), value);
}

/** The value to store for an uploaded file `fileName` in `media`, from an entry at `entryPath`. */
export function mediaValue(fileName: string, media: MediaConfig): string {
  const pub = media.public.replace(/\/$/, '');
  return pub ? `${pub}/${fileName}` : fileName;
}

/** A media config with an empty folder means "next to the entry": make it concrete for one entry file. */
export function entryMedia(media: MediaConfig, entryPath: string): MediaConfig {
  return media.folder === '' ? { folder: dirname(entryPath), public: media.public } : media;
}

/** Repo path where an upload named `fileName` goes. */
export function mediaUploadPath(fileName: string, media: MediaConfig): string {
  return joinPath(media.folder, fileName);
}

/** Safe file name: ascii, lowercase, keeps the extension. */
export function safeFileName(name: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') : '';
  const stem = (dot > 0 ? name.slice(0, dot) : name)
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'image';
  return ext ? `${stem}.${ext}` : stem;
}

export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'svg'];

export function isImagePath(path: string): boolean {
  return IMAGE_EXTENSIONS.includes(path.slice(path.lastIndexOf('.') + 1).toLowerCase());
}
